import { Card, Text, View } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { formatDateTime } from "@src/utils/format-date";
import type { RoadSenseReportMeta } from "@visioni/a3s-reports/client-messages";

interface RoadSenseHeroProps {
  meta: RoadSenseReportMeta;
  /**
   * ISO instant the report row was last marked ready (the generator's
   * `generatedAt`). Reports regenerate daily, so this is the "last updated"
   * freshness signal. Optional/absent for a not-yet-ready row — the line is
   * omitted rather than shown blank in that case.
   */
  generatedAt?: string;
  /**
   * The site's IANA timezone (`payload.site.site_timezone`). The "Generated At"
   * stamp renders in this zone so it matches the rest of the site-local report,
   * rather than the viewer's local time.
   */
  siteTimezone?: string;
}

/**
 * Top-of-report header block for RoadSense. RoadSense-owned (a sibling of, not
 * a fork of, the WasteSense hero). All copy comes pre-formatted from the
 * generator (`report_meta`) so the renderer never reconstructs the title or
 * window from raw bounds — the same strings appear in the static export and
 * this screen.
 */
export function RoadSenseHero({ meta, generatedAt, siteTimezone }: RoadSenseHeroProps) {
  const { theme } = useUnistyles();
  // RoadSense is per-site, so the freshness stamp reads in the site's own zone
  // (consistent with the rest of the report, which is rendered site-locally).
  const generatedAtLabel = formatDateTime(generatedAt, siteTimezone);
  return (
    <View gap="md" alignItems="flex-start">
      <Text textStyle="h3" fontSize={22} style={{ color: theme.colors.red[600] }}>
        {meta.report_title}
      </Text>
      <Text textStyle="sm/regular" color="textMuted">
        {meta.report_subtitle}
      </Text>
      <Text textStyle="sm/regular" color="text">
        Reporting window: {meta.window_label}
      </Text>
      {generatedAtLabel ?
        <Text textStyle="sm/regular" color="textMuted">
          Generated At: {generatedAtLabel}
        </Text>
      : null}
    </View>
  );
}
